import React, {
  useEffect,
  useMemo,
} from 'react';
import {
  Control,
  FormProvider,
  useForm,
} from 'react-hook-form';
import {
  useQueryClient,
} from '@tanstack/react-query';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';
import {
  useNotify,
} from 'core/hooks';
import {
  RTMProviderThresholdsDTO,
} from 'dtos';
import Button from 'pages/Dashboard/components/Button';
import Dialog from 'pages/Dashboard/components/Dialog';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  useGetUsers,
} from 'pages/Dashboard/hooks/providers';
import {
  useAddOrUpdateProviderThresholds,
  useGetProviderThresholds,
} from 'pages/Dashboard/pages/Dashboard/RTM/hooks';
import ThresholdSettingsDialogSkeleton from 'pages/Dashboard/pages/Dashboard/RTM/ThresholdSettings/ThresholdSettingsDialogSkeleton';
import ThresholdSignalRow from 'pages/Dashboard/pages/Dashboard/RTM/ThresholdSettings/ThresholdSignalRow';
import {
  ThresholdSectionConfig,
  thresholdSections,
} from 'pages/Dashboard/pages/Dashboard/RTM/ThresholdSettings/thresholdUiConfig';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import {
  formatDate,
} from 'utils/date';
import Skeleton from 'core/components/Skeleton';

const thresholdRowClassName = 'flex items-center gap-x-4';

type Props = {
  onClose: () => void;
};

type ThresholdSectionProps = {
  section: ThresholdSectionConfig;
  control: Control<RTMProviderThresholdsDTO>;
};

function ThresholdSection({ section, control }: ThresholdSectionProps) {
  return (
    <div>
      {notEmptyString(section.title) && (
        <div className="border-y border-gray-200 bg-gray-50 p-3 text-xs font-semibold uppercase tracking-wide text-gray-700">
          {section.title}
        </div>
      )}
      <div>
        {section.rows.map((row) => (
          <ThresholdSignalRow
            key={row.title}
            row={row}
            control={control}
            gridClassName={thresholdRowClassName}
          />
        ))}
      </div>
    </div>
  );
}

function ThresholdColumnHeaders() {
  return (
    <div className={clsx(thresholdRowClassName, 'px-3 pb-2 text-gray-600')}>
      <span className="w-36 shrink-0 text-xs font-semibold tracking-wide">Signal</span>
      <span className="min-w-0 flex-1 text-xs font-semibold tracking-wide">Watch</span>
      <span className="min-w-0 flex-1 text-xs font-semibold tracking-wide">Needs Review</span>
    </div>
  );
}

export default function ThresholdSettingsDialog({
  onClose,
}: Props) {
  const userInfo = useUserInfo();
  const providerUserId = userInfo?.flags?.rtmDefaultProviderId ?? 0;
  const { data: users = [], isLoading: isLoadingUsers } = useGetUsers({ onlyDoctors: false });
  const providerUser = useMemo(() => (
    users.find((user) => user.userId === providerUserId)
  ), [users, providerUserId]);

  const notify = useNotify();
  const queryClient = useQueryClient();
  const { data, isFetching: isFetchingThresholds } = useGetProviderThresholds(providerUserId);
  const { mutateAsync: saveThresholds, isPending } = useAddOrUpdateProviderThresholds();
  const formProps = useForm<RTMProviderThresholdsDTO>();
  const { control, handleSubmit, reset } = formProps;
  const isFetching = isFetchingThresholds || (isLoadingUsers && isNil(providerUser));

  useEffect(() => {
    if (!isNil(data)) {
      reset(data);
    }
  }, [data, reset]);

  async function onSubmit(values: RTMProviderThresholdsDTO) {
    try {
      await saveThresholds({
        ...values,
        providerUserId,
      });
      await queryClient.invalidateQueries({
        queryKey: ['rtm-provider-thresholds', providerUserId],
      });
      notify({ variant: 'success', message: 'Thresholds updated' });
      onClose();
    } catch {
      // Global mutation onError handles error notify
    }
  }

  return (
    <FormProvider {...formProps}>
      <Dialog
        open
        onClose={onClose}
        title={isFetching ? (
          <div className="min-w-0 !h-12 pr-2 flex flex-col justify-center gap-1.5">
            <Skeleton variant="rectangular" className="!h-6 !w-72 !rounded" />
            <Skeleton variant="rectangular" className="!h-3.5 !w-40 !rounded" />
          </div>
        ) : (
          <div className="min-w-0 !h-12 pr-2">
            <h2 className="text-xl font-semibold text-gray-900">
              Alert thresholds for {getDisplayName(userInfo?.user?.formattedNames, DEFAULT_EMPTY_VALUE)}
            </h2>
            {!isNil(data?.updatedOn) && (
              <p className="text-sm font-medium text-gray-500">
                Last updated on {formatDate(data.updatedOn)}
              </p>
            )}
          </div>
        )}
        maxWidth={false}
        fullWidth
        classes={{
          root: '!w-full !h-screen !min-w-0 flex flex-col !p-0 !space-y-0',
          dialog: 'hidden-backdrop populate-iframe-modal !m-0 settings-form-dialog',
          bodyRoot: '!m-0 !w-full !min-w-0 !max-w-[calc(100%-12px)] !overflow-hidden',
          container: '!justify-start',
          title: 'px-6 pt-6 pb-3 border-b border-gray-100 shrink-0',
        }}
      >
        {isFetching ? (
          <ThresholdSettingsDialogSkeleton />
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto p-6 pb-0">
              <div className="flex flex-col">
                <ThresholdColumnHeaders />
                <div className="flex flex-col border border-gray-200 rounded-md">
                  {thresholdSections.map((section) => (
                    <ThresholdSection
                      key={section.id}
                      section={section}
                      control={control}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex shrink-0 items-center justify-between gap-3 border-t border-gray-200 px-5 py-4">
              <Button
                type="button"
                variant="outlined"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="contained"
                color="primary"
                disabled={isPending}
                onClick={handleSubmit(onSubmit)}
              >
                Apply thresholds
              </Button>
            </div>
          </div>
        )}
      </Dialog>
    </FormProvider>
  );
}
